import { ACTIVE_SMS_ALERT_LIMIT, DAILY_SMS_LIMIT } from "@shared/alert-limits";
import { AGENT_LIMITS } from "@shared/agent-plan";

export interface Article {
  slug: string;
  question: string;
  answer: string;
}

export interface Category {
  slug: string;
  title: string;
  icon: string;
  description: string;
  articles: Article[];
}

export const SUPPORT_CATEGORIES: Category[] = [
  {
    slug: "getting-started",
    title: "Getting Started",
    icon: "🚀",
    description: "New to LiveSwell? Start here.",
    articles: [
      {
        slug: "search-surf-spot",
        question: "How do I search for a surf spot?",
        answer:
          "Open Global Surf Spots and search by spot, city, or region. Select a result to see the conditions available for that location. LiveSwell covers more than 200 spots worldwide, but not every break has a listing or a nearby live sensor. If you can't find a spot, try the town or region name; don't assume a nearby listed break has the same conditions.",
      },
      {
        slug: "save-favorite",
        question: "How do I save a spot to my favorites?",
        answer:
          "Tap the heart on a spot card or its conditions page to save it. Tap the heart again to remove it. Your saved spots appear in the Saved section when you sign in with the same account. Save a spot before using it in a new alert.",
      },
      {
        slug: "wave-height-meaning",
        question: "What does the wave height number mean?",
        answer:
          "The source matters. A live NOAA buoy's significant wave height is measured at that buoy, not at the beach. Where a live buoy reading is unavailable, the app may show a labeled marine-model estimate instead. Forecast-card Swell values are model significant wave heights at specific hours. Neither number predicts exact breaking-wave or face height at your spot: direction, seafloor, and local conditions affect the surf.",
      },
      {
        slug: "data-update-frequency",
        question: "How is the data updated?",
        answer:
          "Live readings update when their source reports new data; NOAA stations do not all report on the same schedule. Check the displayed timestamp and use the page's refresh control if a reading looks old. Wind and forecast values may come from different providers and have different update times. Tides are predictions, not live measurements, and can be unavailable when a suitable source cannot be verified. LiveSwell does not guarantee that every reading or summary changes every 30 minutes.",
      },
    ],
  },
  {
    slug: "conditions-data",
    title: "Conditions Data",
    icon: "🌊",
    description: "Understanding wave, wind, and swell readings.",
    articles: [
      {
        slug: "wave-data-source",
        question: "Where does the wave data come from?",
        answer:
          "Where available, current readings come from NOAA NDBC buoys or stations, with the station identified on the conditions page. In areas without suitable live buoy coverage, LiveSwell can show a labeled Open-Meteo Marine model estimate instead; that is not an observation. The Swell values on the forecast cards are model values, even at spots that have a live buoy. Check the source shown with each reading before comparing it with what you see at the beach.",
      },
      {
        slug: "swell-period",
        question: "What is swell period and why does it matter?",
        answer:
          "Wave period is the time in seconds between wave crests at the station or in the model. A longer period can indicate swell from a more distant storm; short periods often reflect local wind waves. Period alone does not tell you how waves will break at the beach. Height, direction, wind, and the seafloor also matter. Check whether the value is an observed buoy reading or a forecast model estimate.",
      },
      {
        slug: "swell-direction",
        question: "What does swell direction mean for my break?",
        answer:
          "Wave direction is the compass bearing the waves are coming from, so a 270° reading means waves arriving from the west. Whether they reach your spot depends on coastline orientation, sheltering, and how the swell refracts. Use the displayed direction alongside your knowledge of the break; it is not a guarantee of surf quality. For forecasts, the bearing is a model estimate rather than a buoy observation.",
      },
      {
        slug: "two-buoys",
        question: "Why does my spot show two buoys?",
        answer:
          "Some spots display readings from more than one NOAA buoy. Their station names and readings help you compare different points in the surrounding water. Neither buoy is a direct measurement of breaking waves at the spot; distance alone does not guarantee that one reading is more representative than another.",
      },
      {
        slug: "ai-summary-accuracy",
        question: "How accurate is the LiveSwell Agent surf summary?",
        answer:
          "Agent describes the available conditions but cannot know exactly how a break is surfing. For questions about the past, it uses recorded buoy or station observations where available; it should say when recorded history is unavailable, not substitute a past model estimate. Future waves and wind are model forecasts, not observations, and tide times are predictions. Your question, relevant conditions, and limited recent conversation context are sent to Anthropic's Claude to help generate answers; requests do not attach your phone number or LiveSwell account identifier as separate fields. Personal details you type can still be sent as part of your message, so do not include sensitive information. In-app and SMS histories are separate. AI replies may be incomplete or inaccurate and are not safety advice.",
      },
    ],
  },
  {
    slug: "sms-alerts",
    title: "SMS Alerts",
    icon: "📱",
    description: "Set up reports and alerts by SMS, email, or push.",
    articles: [
      {
        slug: "setup-alert",
        question: "How do I set up a condition alert?",
        answer:
          "Save the spot, then open Profile → Notifications (Alerts) and tap Add Alert. Choose the spot, alert type, conditions or schedule, and delivery channels. For SMS, verify your number with the one-time code and confirm your home time zone; email and push do not require SMS verification. Live conditions alerts check for qualifying readings periodically, but matching SMS alerts are not sent repeatedly throughout the day. A missing or untrusted reading is not treated as a match.",
      },
      {
        slug: "alert-thresholds",
        question: "What thresholds can I set?",
        answer:
          "Choose the alert type that fits: Daily Report at a scheduled time; Swell for wave height, period, or direction; Wind for a speed or direction; Tide ahead of a predicted high or low; Forecast Outlook for a scheduled look at tomorrow; or Forecast Match when tomorrow's modeled waves and wind meet your selected range. The form shows the options available for that type. Forecast values are estimates, not current buoy measurements, and tide-trigger availability depends on a verified tide source for the spot.",
      },
      {
        slug: "stop-alerts",
        question: "How do I stop receiving alerts?",
        answer:
          "To stop an individual alert, open Profile → Notifications and pause or delete it, or turn off its SMS delivery. To stop all texts to your number, reply STOP to a LiveSwell SMS; this blocks delivery at the carrier. Replying START may restore carrier permission, but check the app's opt-out notice, number verification, and each alert's SMS setting before expecting texts again. Turning off SMS for an alert does not necessarily turn off its selected email or push delivery.",
      },
      {
        slug: "missed-alert",
        question: "Why didn't I get an alert when conditions were good?",
        answer:
          `Check Profile → Notifications: is the alert active for the right spot and channel, and are your thresholds and schedule correct? For SMS, confirm your phone number and saved home time zone, and check for an opt-out notice if you sent STOP. Pro allows up to ${DAILY_SMS_LIMIT} automated alert texts per home day; Agent allows up to ${AGENT_LIMITS.dailyAlertSms}. A matching alert can text at most once per alert per home day, and a separate monthly segment allowance can pause SMS. The day resets at 12:01 a.m. in your confirmed home time zone. Missing, stale, or unsupported source data cannot trigger a qualifying alert; selected email and push may still be delivered when SMS is paused.`,
      },
      {
        slug: "multiple-spots",
        question: "Can I set alerts for multiple spots?",
        answer:
          `Yes. Save the spots first, then manage their alerts in Profile → Notifications. Pro allows up to ${ACTIVE_SMS_ALERT_LIMIT} active SMS-enabled alert settings per account, and Agent allows up to ${AGENT_LIMITS.activeAlerts}; the count includes scheduled reports and other alert types using SMS. Pause an alert or remove its SMS channel to free a setting. Email-only and push-only settings do not use an SMS setting slot. Automated SMS deliveries also have a separate daily allowance.`,
      },
      {
        slug: "text-the-liveswell-agent",
        question: "What can I ask the LiveSwell Agent by text?",
        answer:
          `Agent members can ask about conditions or available forecasts by SMS or in-app chat. Questions about past conditions use recorded buoy or station observations where available; if none are available, Agent should say so rather than substitute a past model estimate. Tomorrow's waves and wind are model forecasts; tides are predictions. Agent includes up to ${AGENT_LIMITS.includedReplies} outgoing conversational SMS replies per billing month; your incoming messages do not count, and automated alert texts are separate. Extra Agent SMS reply collection is currently disabled, so replies pause when the included allowance is used. Account & Billing shows usage and retained billing history. In-app chat is subject to fair-use limits and keeps a separate history from SMS. Replies may be incomplete or inaccurate and are not safety advice.`,
      },
    ],
  },
  {
    slug: "agent",
    title: "LiveSwell Agent",
    icon: "💬",
    description: "In-app chat, SMS replies, and usage.",
    articles: [
      {
        slug: "agent-access",
        question: "How do I use LiveSwell Agent?",
        answer:
          "Agent membership includes in-app chat and SMS conversations, subject to fair-use limits for chat. Open Agent chat in the app or text the LiveSwell number associated with your verified account. In-app chat and SMS have separate histories. Ask about available current conditions, forecast estimates, or recorded past buoy and station readings; when trusted historical observations are unavailable, Agent should tell you rather than present old model estimates as facts. Agent responses can be incomplete or inaccurate and are not safety advice.",
      },
      {
        slug: "agent-sms-usage",
        question: "What happens after my included Agent SMS replies?",
        answer:
          `Agent includes ${AGENT_LIMITS.includedReplies} outgoing conversational replies per billing month. Your incoming texts do not use that reply count, and automated alert texts have their own allowance. Extra Agent SMS reply collection is currently disabled, so replies pause when the included allowance runs out. Check Account & Billing for current usage and retained billing history. In-app chat remains subject to its own fair-use limits.`,
      },
    ],
  },
  {
    slug: "tides-forecasts",
    title: "Tides & Forecasts",
    icon: "🌙",
    description: "Reading tide charts and forecast data.",
    articles: [
      {
        slug: "read-tide-chart",
        question: "How do I read the tide chart?",
        answer:
          "The chart plots predicted high and low tide times and heights for the selected day and draws a curve between those events; the curve is not a live water-level measurement. NOAA heights are typically relative to MLLW, while WorldTides uses the datum supplied with its prediction, so heights from different providers should not be compared as if they use the same baseline. Check the labeled high and low events for predicted times and heights. Depending on the spot and day, the chart also marks sunrise and sunset. If no reliable predictions are available, LiveSwell leaves the tide details unavailable rather than inventing a curve.",
      },
      {
        slug: "forecast-range",
        question: "How far ahead does the forecast go?",
        answer:
          "LiveSwell's near-term wave and wind cards use available model hours within a 168-hour horizon, not weeks-ahead data. Each available day shows Swell and Wind at 6 AM, noon, and 6 PM in that spot's local time. A missing hour is shown as unavailable rather than filled from another hour. These are model significant wave heights, not observed buoys or exact breaking surf; accuracy has not been validated for each beach, and longer lead times are more uncertain. Tide predictions appear only when a trusted NOAA or eligible WorldTides source has events for that day; they do not extend the wave and wind forecast to days 8–14.",
      },
      {
        slug: "sunrise-sunset-line",
        question: "What is the sunrise/sunset line on the tide chart?",
        answer:
          "Where shown, the sunrise and sunset markers help you compare daylight with predicted tide times at the selected spot. They do not indicate measured tide levels or guarantee surf conditions at that time. If sunrise or sunset data is unavailable, those markers may be absent.",
      },
    ],
  },
  {
    slug: "account-privacy",
    title: "Account & Billing",
    icon: "🔐",
    description: "Plans, billing, phone number, and privacy.",
    articles: [
      {
        slug: "manage-plan",
        question: "Where can I see my plan or manage billing?",
        answer:
          "Open Profile → Account & Billing to see current access and manage your Paddle subscription. Pro is $8 USD per month and Agent is $20 USD per month, plus applicable taxes. Eligible subscribers may receive a seven-day trial; checkout determines eligibility and repeat trials are not promised. Plans renew monthly after any trial until canceled. A failed renewal on a previously paid subscription has a 72-hour grace period; this does not apply to initial or trial-ending payments. Deleting an alert does not cancel a subscription. If billing details look wrong, use the contact form below and do not start a second checkout to fix them.",
      },
      {
        slug: "delete-account",
        question: "How do I delete my account?",
        answer:
          "Open Profile and choose Delete my account near the bottom. Follow the confirmation prompts. LiveSwell handles attached subscriptions before removing the account; if cancellation or sign-in removal cannot be confirmed, follow the error instructions or contact support rather than assuming deletion finished. Completed deletion removes your sign-in, saved spots, alerts, and local conversations and cannot be undone. Some opt-out protection, hashed deleted-account identifiers, billing-operation records, and provider-held records can remain; see the Privacy Policy. If you only want to stop texts, change your alert settings under Profile → Notifications or reply STOP instead.",
      },
      {
        slug: "data-collected",
        question: "What data does LiveSwell collect?",
        answer:
          "LiveSwell uses account details such as your name and email for sign-in through Clerk, your saved spots and alert preferences, and usage data to operate the app. If you enable SMS alerts or Agent texting, it also uses your verified phone number and the messages needed to deliver that service. In-app Agent questions and replies are stored separately from SMS; limited recent context is sent to Anthropic's Claude for answers. New chat clears stored in-app history, not SMS or provider-held records. Paddle handles web billing; LiveSwell keeps account-linked subscription, payment-status, usage, invoice, and historical overage-consent records. Extra Agent SMS reply collection is currently disabled. Optional location, push delivery, support messages, and error diagnostics also involve data described in the Privacy Policy. Read that policy for providers, cleanup rules, and your choices. We do not sell your personal information for advertising.",
      },
      {
        slug: "update-phone-number",
        question: "How do I update my phone number?",
        answer:
          "Open Profile → Notifications and edit the phone number used for SMS. Verify the new number with a one-time code, save your home time zone, and check which alerts have SMS enabled. A STOP opt-out on your old number does not authorize sending to a new unverified number. If texts still do not arrive after verification, use the contact form below.",
      },
    ],
  },
  {
    slug: "troubleshooting",
    title: "Troubleshooting",
    icon: "🔧",
    description: "Fix common issues quickly.",
    articles: [
      {
        slug: "no-data-available",
        question: "The app says \"no data available\" for my spot",
        answer:
          "A buoy or forecast provider may have a gap, or that spot may not have a suitable verified station. Look at the source and last-updated time for the affected reading. If a current buoy is unavailable, LiveSwell may show a clearly labeled marine-model estimate where coverage allows, or mark the value unavailable; it should not fabricate a buoy observation. Missing tide predictions are kept unavailable instead of inventing events. Try again later or use the contact form with the spot name and the data that is missing.",
      },
      {
        slug: "sms-alerts-stopped",
        question: "My SMS alerts stopped working",
        answer:
          "Check the Alerts screen for an SMS-paused or opt-out notice and confirm your home time zone, verified phone number, and each alert's active status and SMS channel. If you replied STOP, carrier permission and your app setting may both need attention; replying START alone does not automatically reactivate every alert. Check whether the matching alert already texted today or your plan's daily or monthly SMS allowance is exhausted. If the source readings were missing or your conditions did not match, the alert will not fire. Use the contact form with the spot and alert type if you still need help; never put a verification code in the message.",
      },
      {
        slug: "conditions-not-updated",
        question: "The conditions haven't updated in a while",
        answer:
          "Check the timestamp and source shown with the reading, then use the refresh control or reopen the spot. A refresh cannot create a new NOAA observation before the station reports one; modeled values and tide predictions have different update schedules. If a source is unavailable, it may show an unavailable state rather than old data presented as current. If one spot repeatedly looks wrong, use the contact form and include the spot, the affected reading, and the timestamp you see.",
      },
      {
        slug: "app-slow",
        question: "The app is slow or not loading",
        answer:
          "Check your connection, reopen the app or browser tab, and refresh the page. Live conditions depend on external data sources, so an individual reading may take longer or be temporarily unavailable. If you see a sign-in error, start a fresh sign-in from the app rather than reusing an old callback or error URL. If the problem persists, use the contact form below with your device, spot, and the error message you see. Do not send passwords or verification codes.",
      },
    ],
  },
];

export function getAllArticles(): Array<Article & { categorySlug: string; categoryTitle: string }> {
  return SUPPORT_CATEGORIES.flatMap(cat =>
    cat.articles.map(article => ({
      ...article,
      categorySlug: cat.slug,
      categoryTitle: cat.title,
    }))
  );
}

export function getCategoryBySlug(slug: string): Category | undefined {
  return SUPPORT_CATEGORIES.find(c => c.slug === slug);
}
